import React from 'react';

import {
  Input,
  DropdownButton,
  DropdownItem,
  useTranslation,
  PasswordInput,
  RadioGroup,
  Radio,
} from '@testsigma/atoms';

import {
  getSalesforceEditionI18nKey,
  getSalesforceLoginMethodI18nKey,
  SalesforceConnectionEdition,
  SalesforceLoginMethod,
} from '@modules/salesforce/models';

import { OAuthFields } from './oauth-fields';
import { UsernamePasswordFields } from './username-password-fields';
import { ProjectVersionFields } from './project-version-fields.js';

interface ConnectionFormBodyProps {
  /** values object provided by react-hook-form */
  values: any;
  /** The watch function from react-hook-form used to retrieve the latest value from the form state */
  watch: (field: string) => any;
  /** Function to update the form field identified by fieldId with the value fieldValue*/
  handleUpdateFormValue?: (fieldId: string, fieldValue: any) => void;
}

/**
 * Renders the form body UI for Metadata/User connections, used in creating, editing, and viewing both Metadata and User connections.
 * @param values props.values values object provided by react-hook-form
 * @param watch props.watch The watch function from react-hook-form used to retrieve the latest value from the form state
 * @param props.handleUpdateFormValue Function to update the form field identified by fieldId with the value fieldValue
 *   - **Parameters:**
 *     - `fieldId` (string): The identifier of the form field to be updated.
 *     - `fieldValue` (any): The new value to set for the specified form field.
 *
 *   - **Returns:**
 *     - `void`: This function does not return a value; it updates the form field with the new value.
 *
 * @returns The form body UI for Metadata/User connections
 */
const ConnectionFormBody: React.FC<ConnectionFormBodyProps> = (
  props: ConnectionFormBodyProps,
) => {
  const { watch, values, handleUpdateFormValue } = props;

  const { t } = useTranslation();

  return (
    <div className='px-8 py-2 w-full flex flex-col h-full justify-start'>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('project-version.name.label')}
        </div>
        <div className='!w-[320px]'>
          <ProjectVersionFields />
        </div>
      </div>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('connection-name.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='text'
          onChange={(value) => handleUpdateFormValue?.('name', value)}
          hideErrorMessage={true}
          value={watch('name')}
          placeholder = {t('connection-name.label')}
        />
      </div>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('connect-with.label')}
        </div>
        <div className='w-[320px]'>
          <DropdownButton
            className={'!h-7'}
            wrapperClassName={'!h-7'}
            name={
              values['salesforceConnectionEdition'] ? (
                t(
                  getSalesforceEditionI18nKey(
                    values['salesforceConnectionEdition'],
                  ),
                )
              ) : (
                <span className=' font-normal text-neutral-600 leading-4'>
                  {t('common.select')}
                </span>
              )
            }
            placement={'bottom-start'}
          >
            {Object.keys(SalesforceConnectionEdition).map((edition) => {
              const isSelected =
                values['salesforceConnectionEdition'] == edition;
              return (
                <DropdownItem
                  key={edition}
                  onClick={() =>
                    handleUpdateFormValue?.(
                      'salesforceConnectionEdition',
                      edition,
                    )
                  }
                  isSelected={isSelected}
                  className='truncate'
                >
                  {t(getSalesforceEditionI18nKey(edition))}
                </DropdownItem>
              );
            })}
          </DropdownButton>
        </div>
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('authentication-type.label')}
        </div>
        <div className='w-[320px]'>
          <DropdownButton
            className={'!h-7'}
            wrapperClassName={'!h-7'}
            name={
              values['loginMethod']
                ? t(getSalesforceLoginMethodI18nKey(values['loginMethod']))
                : ''
            }
            placement={'bottom-start'}
          >
            {Object.keys(SalesforceLoginMethod).map((loginMethod) => {
              const isSelected = values['loginMethod'] == loginMethod;
              return (
                <DropdownItem
                  key={loginMethod}
                  onClick={() =>
                    handleUpdateFormValue?.('loginMethod', loginMethod)
                  }
                  isSelected={isSelected}
                  className='truncate'
                >
                  {t(getSalesforceLoginMethodI18nKey(loginMethod))}
                </DropdownItem>
              );
            })}
          </DropdownButton>
        </div>
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('User is an admin')}
        </div>

        <RadioGroup
          onChange={(e: React.ChangeEvent<HTMLInputElement> | any) => {
            handleUpdateFormValue?.('isAdmin', e.target?.value == 'true');
          }}
          value={values['isAdmin']}
          name='isAdmin'
        >
          <Radio
            name='true'
            className='mr-8 text-secondary-600'
            value={'true'}
            checked={values['isAdmin']?.toString() == 'true'}
          >
            {t('Yes')}
          </Radio>
          <Radio
            name='false'
            className='mr-8 text-secondary-600'
            value={'false'}
            checked={values['isAdmin']?.toString() == 'false'}
          >
            {t('No')}
          </Radio>
        </RadioGroup>
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('username.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='text'
          onChange={(value) => handleUpdateFormValue?.('username', value)}
          hideErrorMessage={true}
          value={watch('username')}
          placeholder = {t('username.label')}
        />
      </div>

      {values['loginMethod'] == SalesforceLoginMethod.USERNAME_PASSWORD && (
        <UsernamePasswordFields
          handleUpdateFormValue={handleUpdateFormValue}
          watch={watch}
        />
      )}

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('consumer-key.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='text'
          onChange={(value: string) =>
            handleUpdateFormValue?.('clientId', value)
          }
          hideErrorMessage={true}
          value={watch('clientId')}
          placeholder = {t('consumer-key.label')}
        />
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('consumer-secret.label')}
        </div>
        <PasswordInput
          isRequired={true}
          className='w-[320px] h-7'
          type='password'
          hasMask={true}
          onChange={(value: string) =>
            handleUpdateFormValue('clientSecret', value)
          }
          hideErrorMessage={true}
          value={watch('clientSecret')}
          showOutline={true}
          placeholder = {t('consumer-secret.label')}
        />
      </div>

      {values['loginMethod'] == SalesforceLoginMethod.OAUTH && (
        <OAuthFields
          handleUpdateFormValue={handleUpdateFormValue}
          watch={watch}
        />
      )}
    </div>
  );
};

export { ConnectionFormBody };
